import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import { connect } from 'react-redux';
import {
    CallEnd as CallEndIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    VolumeOff as VolumeOffIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';
import videocallshopApi from '../apis/videocallshop';
import QRProductButton from './QRProductButton';
import CustomerLogin from './CustomerLogin';
import useQRDetection from '../hooks/useQRDetection';
import useFullCatalog from './showcase/useFullCatalog';
import ProductCarousel from './showcase/ProductCarousel';
import PinnedSpotlight from './showcase/PinnedSpotlight';
import ProductQuickDialog from './showcase/ProductQuickDialog';
import InCallCart from './cart/InCallCart';

/* global OT */

const SESSION_STATE_POLL_MS = 2000;
const URL_REGEX = /https?:\/\/[^\s]+/g;

function parseTextWithLinks(text) {
    const parts = [];
    let lastIndex = 0;
    let m;
    const re = new RegExp(URL_REGEX.source, 'g');
    while ((m = re.exec(text)) !== null) {
        if (m.index > lastIndex) {
            parts.push({ text: text.slice(lastIndex, m.index), url: null });
        }
        parts.push({ text: m[0], url: m[0] });
        lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) {
        parts.push({ text: text.slice(lastIndex), url: null });
    }
    if (parts.length === 0 && text) parts.push({ text, url: null });
    return parts;
}

function LinkableText({ text, sx = {} }) {
    const parts = parseTextWithLinks(text);
    return (
        <Typography component="span" sx={{ fontSize: 'inherit', ...sx }}>
            {parts.map((p, i) =>
                p.url ? (
                    <Typography key={i} component="a" href={p.url} target="_blank" rel="noopener noreferrer"
                        sx={{ color: '#5BB6EE', textDecoration: 'underline', cursor: 'pointer', '&:hover': { color: '#8FD0F5' } }}>
                        {p.text}
                    </Typography>
                ) : (
                    <Typography key={i} component="span">{p.text}</Typography>
                )
            )}
        </Typography>
    );
}

function generateQuestionId() {
    return 'q-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);
}

// Watching a live requires a logged-in customer (the API gates
// viewer-session/session-state/questions); identity is server-derived from
// x-customer-token, the client no longer asserts viewerId/authorName.
const LiveViewer = ({ customer }) => {
    const { storeId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(false);
    const [error, setError] = useState(null);
    const [sessionData, setSessionData] = useState(null);
    const [callEnded, setCallEnded] = useState(false);
    const [audioBlocked, setAudioBlocked] = useState(false);

    const { url: qrUrl, dismiss: dismissQrUrl } = useQRDetection({
        containerId: 'subscriber',
        enabled: active && !callEnded && !loading,
    });

    // Refs
    const sessionRef = useRef(null);
    const subscriberRef = useRef(null);
    const intentionalDisconnectRef = useRef(false);
    const isMountedRef = useRef(true);
    const touchStartX = useRef(null);
    const touchStartY = useRef(null);

    const [qaItems, setQaItems] = useState([]);
    // Lo que la tienda muestra ahora: un producto pineado o un link manual.
    const [pinnedProduct, setPinnedProduct] = useState(null);
    const [pinnedUrl, setPinnedUrl] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [questionInput, setQuestionInput] = useState('');
    const qaScrollRef = useRef(null);
    const lastQaSnapshotRef = useRef(null);

    const catalog = useFullCatalog(customer && active ? storeId : null);

    const [liveCalls, setLiveCalls] = useState([]);

    // Suprimir errores de autoplay
    useEffect(() => {
        const handler = (e) => {
            if (
                e?.message?.includes('play method is not allowed') ||
                e?.message?.includes('play() request was interrupted') ||
                e?.reason?.message?.includes('play method is not allowed') ||
                e?.reason?.message?.includes('play() request was interrupted')
            ) {
                e.preventDefault();
                e.stopImmediatePropagation();
                return false;
            }
        };
        window.addEventListener('error', handler, true);
        window.addEventListener('unhandledrejection', handler, true);
        return () => {
            window.removeEventListener('error', handler, true);
            window.removeEventListener('unhandledrejection', handler, true);
        };
    }, []);

    // isMounted tracker
    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    const isOwnQuestion = useCallback((item) => {
        if (item.kind !== 'question') return false;
        if (!customer) return false;
        return String(item.viewerId) === String(customer.customerId);
    }, [customer]);

    const sendQuestion = useCallback(async (text) => {
        if (!storeId || !text?.trim()) return;
        const id = generateQuestionId();
        const trimmedText = text.trim();
        const authorName = customer
            ? [customer.name, customer.lastName].filter(Boolean).join(' ')
            : '';
        const newItem = {
            kind: 'question', id, text: trimmedText,
            authorName, viewerId: customer ? String(customer.customerId) : '',
        };
        try {
            await videocallshopApi.post(
                `/stores/${storeId}/live-calls/active/questions`,
                { question: { id, text: trimmedText } }
            );
            setQuestionInput('');
            setQaItems((prev) => [...prev, newItem]);
            lastQaSnapshotRef.current = null;
        } catch (err) {
            toastr.error('Error', 'No se pudo enviar la pregunta.');
        }
    }, [storeId, customer]);

    const handleSendQuestion = useCallback(() => {
        const text = questionInput.trim();
        if (!text) return;
        sendQuestion(text);
    }, [questionInput, sendQuestion]);

    // Fetch lista de lives para navegación
    useEffect(() => {
        videocallshopApi.get('/live-calls')
            .then(({ data }) => setLiveCalls(data.data || []))
            .catch(() => setLiveCalls([]));
    }, [storeId]);

    // Fetch sesión del viewer — se resetea completamente al cambiar storeId
    useEffect(() => {
        if (!storeId) { navigate('/home'); return; }
        if (!customer) return; // gated: el login panel se muestra en el render

        // Desconectar sesión anterior si existe
        if (sessionRef.current) {
            intentionalDisconnectRef.current = true;
            sessionRef.current.disconnect();
            sessionRef.current = null;
        }

        // Resetear todo el estado
        let cancelled = false;
        setLoading(true);
        setActive(false);
        setError(null);
        setSessionData(null);
        setCallEnded(false);
        setQaItems([]);
        setPinnedProduct(null);
        setPinnedUrl(null);
        setSelectedProduct(null);
        setQuestionInput('');
        setAudioBlocked(false);
        lastQaSnapshotRef.current = null;

        (async () => {
            try {
                const { data } = await videocallshopApi.get(`/stores/${storeId}/live-calls/viewer-session`);
                if (cancelled) return;
                setActive(!!data.data?.active);
                setSessionData(data.data);
                setError(
                    data.data?.active
                        ? null
                        : data.data?.message || 'No hay transmisión activa en este momento.'
                );
            } catch (err) {
                if (!cancelled) {
                    setActive(false);
                    setError('No hay transmisión activa en este momento.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [storeId, customer, navigate]);

    // Poll session state
    useEffect(() => {
        if (!storeId || !customer || !active || loading || callEnded) return;
        let cancelled = false;

        const poll = async () => {
            if (document.visibilityState === 'hidden') return;
            if (cancelled || !isMountedRef.current) return;
            try {
                const { data } = await videocallshopApi.get(`/stores/${storeId}/live-calls/active/session-state`);
                if (cancelled || !isMountedRef.current) return;
                const payload = data?.data;
                if (!payload?.active) {
                    if (cancelled || !isMountedRef.current) return;
                    toastr.info('Transmisión finalizada', 'La transmisión en vivo ha finalizado.');
                    navigate('/home');
                    return;
                }
                const snap = payload.qaSnapshot || {};
                const items = Array.isArray(snap.items) ? snap.items : [];
                // Con un producto pineado, qaSnapshot.productLink es la página
                // de la webapp escrita para las apps viejas: no se usa. Una API
                // sin pinnedUrl (deploy viejo) → el link del snapshot.
                const product = payload.pinnedProduct || null;
                const link = product
                    ? null
                    : payload.pinnedUrl !== undefined
                    ? payload.pinnedUrl
                    : snap.productLink ?? null;
                const key = JSON.stringify({ items, product, link });
                if (lastQaSnapshotRef.current !== key) {
                    lastQaSnapshotRef.current = key;
                    setQaItems(items);
                    setPinnedProduct(product);
                    setPinnedUrl(link);
                }
            } catch (_) {}
        };

        poll();
        const interval = setInterval(poll, SESSION_STATE_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [storeId, customer, active, loading, callEnded, navigate]);

    // Inicializar OpenTok
    useEffect(() => {
        if (loading || !active || !sessionData?.tokboxSessionId || !sessionData?.tokboxTokenViewer || callEnded) return;
        if (typeof OT === 'undefined') { setError('Video player not loaded.'); return; }
        if (!sessionData.tokboxApiKey) { setError('Video configuration missing.'); return; }

        intentionalDisconnectRef.current = false;

        const session = OT.initSession(sessionData.tokboxApiKey, sessionData.tokboxSessionId);
        sessionRef.current = session;

        session.on('streamCreated', (event) => {
            const subscriber = session.subscribe(
                event.stream, 'subscriber',
                { insertMode: 'append', width: '100%', height: '100%', subscribeToAudio: true,preferredResolution: { width: 1920, height: 1080 },
preferredFrameRate: 30, },
                (err) => err && setError(err.message)
            );
            subscriberRef.current = subscriber;

            // Browsers may block audio autoplay; OT fires audioBlocked when
            // that happens and silences the <video> element. Without this
            // handler the user has to click OT's default speaker icon twice
            // (first click only re-syncs OT's internal state, second click
            // lands in a fresh user-gesture context and finally plays).
            subscriber.on('audioBlocked', () => setAudioBlocked(true));
            subscriber.on('audioUnblocked', () => setAudioBlocked(false));
        });

        session.on('sessionDisconnected', () => {
            if (intentionalDisconnectRef.current) {
                intentionalDisconnectRef.current = false;
                return;
            }
            if (!isMountedRef.current) return;
            setCallEnded(true);
            toastr.info('Transmisión finalizada', 'La transmisión en vivo ha finalizado.');
            navigate('/home');
        });

        session.on('signal', () => {});

        session.connect(sessionData.tokboxTokenViewer, (err) => {
            if (err) setError(err.message || 'Failed to connect.');
        });

        return () => {
            if (sessionRef.current) {
                intentionalDisconnectRef.current = true;
                sessionRef.current.disconnect();
                sessionRef.current = null;
            }
        };
    }, [loading, active, sessionData, callEnded, navigate]);

    useEffect(() => {
        if (qaItems.length > 0) qaScrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [qaItems.length]);

    // Navegación entre lives
    const currentIndex = liveCalls.findIndex(lc => String(lc.storeId) === String(storeId));
    const prevLive = currentIndex > 0 ? liveCalls[currentIndex - 1] : null;
    const nextLive = currentIndex < liveCalls.length - 1 ? liveCalls[currentIndex + 1] : null;

    const goToLive = (targetStoreId) => {
        intentionalDisconnectRef.current = true;
        setCallEnded(false);
        setActive(false);
        setSessionData(null);
        navigate(`/store/${targetStoreId}/live`);
    };

    const handleEnableAudio = () => {
        if (subscriberRef.current) {
            subscriberRef.current.subscribeToAudio(true);
        }
        setAudioBlocked(false);
    };

    // Swipe handlers
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const deltaX = e.changedTouches[0].clientX - touchStartX.current;
        const deltaY = e.changedTouches[0].clientY - touchStartY.current;
        if (Math.abs(deltaX) < Math.abs(deltaY)) return;
        if (Math.abs(deltaX) < 60) return;
        if (deltaX < 0 && nextLive) {
            goToLive(nextLive.storeId);
        } else if (deltaX > 0 && prevLive) {
            goToLive(prevLive.storeId);
        }
        touchStartX.current = null;
        touchStartY.current = null;
    };

    if (!customer) {
        return (
            <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#000', display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 450, display: 'flex', flexDirection: 'column', justifyContent: 'center', px: 2 }}>
                    <CustomerLogin
                        title="Iniciá sesión para ver el vivo"
                        subtitle="Para entrar a la transmisión necesitás una cuenta. Usá Google o Apple, es gratis."
                    />
                    <Button sx={{ mt: 2 }} onClick={() => navigate('/home')}>
                        Volver al inicio
                    </Button>
                </Box>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#000', display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 450, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: 'white' }}>Cargando transmisión...</Typography>
                </Box>
            </Box>
        );
    }

    if (!active || error) {
        return (
            <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#000', display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 450, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Typography color="text.secondary">{error || 'No hay transmisión activa en este momento.'}</Typography>
                    <Button variant="contained" onClick={() => navigate('/home')}>Volver al inicio</Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#000', display: 'flex', justifyContent: 'center' }}>
        <Box
            sx={{ width: '100%', maxWidth: 450, minHeight: '100vh', bgcolor: '#000', position: 'relative' }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <Box id="subscriber" sx={{ width: '100%', height: '100vh' }} />

            {/* Flecha ANTERIOR */}
            {prevLive && (
                <Tooltip title="Live anterior" placement="right">
                    <IconButton
                        onClick={() => goToLive(prevLive.storeId)}
                        sx={{
                            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                            zIndex: 30, backgroundColor: 'rgba(0,0,0,0.7)',
                            border: '1px solid rgba(255,255,255,0.2)', color: 'white',
                            width: 48, height: 48,
                            '&:hover': { backgroundColor: '#ff4444', borderColor: '#ff4444' },
                            transition: 'all 0.2s',
                        }}
                    >
                        <ChevronLeftIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                </Tooltip>
            )}

            {/* Flecha SIGUIENTE */}
            {nextLive && (
                <Tooltip title="Siguiente live" placement="left">
                    <IconButton
                        onClick={() => goToLive(nextLive.storeId)}
                        sx={{
                            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                            zIndex: 30, backgroundColor: 'rgba(0,0,0,0.7)',
                            border: '1px solid rgba(255,255,255,0.2)', color: 'white',
                            width: 48, height: 48,
                            '&:hover': { backgroundColor: '#ff4444', borderColor: '#ff4444' },
                            transition: 'all 0.2s',
                        }}
                    >
                        <ChevronRightIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                </Tooltip>
            )}

            {/* Indicador de posición */}
            {liveCalls.length > 1 && (
                <Box sx={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 25, display: 'flex', gap: 1 }}>
                    {liveCalls.map((lc, i) => (
                        <Box
                            key={lc.liveCallId}
                            onClick={() => goToLive(lc.storeId)}
                            sx={{
                                width: i === currentIndex ? 24 : 8, height: 4, borderRadius: 2,
                                cursor: 'pointer',
                                backgroundColor: i === currentIndex ? '#ff4444' : 'rgba(255,255,255,0.4)',
                                transition: 'all 0.3s ease',
                            }}
                        />
                    ))}
                </Box>
            )}

            {audioBlocked && (
                <Box sx={{
                    position: 'absolute',
                    top: liveCalls.length > 1 ? 40 : 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 30,
                }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<VolumeOffIcon />}
                        onClick={handleEnableAudio}
                    >
                        Activar audio
                    </Button>
                </Box>
            )}

            {/* Lo que la tienda está mostrando (producto pineado o link) */}
            <PinnedSpotlight
                pinnedProduct={pinnedProduct}
                pinnedUrl={pinnedUrl}
                onOpenProduct={setSelectedProduct}
                sx={{ position: 'absolute', top: audioBlocked ? 96 : 80, left: 12, right: 12, width: 'auto', zIndex: 15 }}
            />

            {/* QR-detected product button */}
            <QRProductButton
                url={qrUrl}
                onDismiss={dismissQrUrl}
                sx={{ position: 'absolute', bottom: 240, zIndex: 25 }}
            />

            {/* Panel Q&A, con el carrusel de productos arriba */}
            <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                minHeight: 220, maxHeight: catalog.length > 0 ? '58%' : '45%', zIndex: 20,
                background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
                borderRadius: '12px 12px 0 0', display: 'flex', flexDirection: 'column',
            }}>
                <ProductCarousel
                    products={catalog}
                    pinnedProductId={pinnedProduct && pinnedProduct.productId}
                    onSelect={setSelectedProduct}
                    sx={{ flexShrink: 0, pt: 1 }}
                />
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 600, px: 2, py: 1.5 }}>
                    Preguntas y respuestas
                </Typography>
                <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0, px: 2, pb: 1 }}>
                    {qaItems.length === 0 ? (
                        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, py: 2 }}>
                            Aún no hay preguntas. ¡Sé el primero!
                        </Typography>
                    ) : (
                        qaItems.map((item) => {
                            if (item.kind === 'question') {
                                const q = item;
                                const own = isOwnQuestion(q);
                                return (
                                    <Box key={`q-${q.id}`} sx={{ mb: 1.5 }}>
                                        <Box sx={{
                                            p: 1, borderRadius: 1,
                                            bgcolor: own ? 'rgba(0,123,255,0.15)' : 'rgba(255,255,255,0.06)',
                                            borderLeft: own ? '4px solid #1D98E7' : '4px solid transparent',
                                        }}>
                                            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 600, mr: 0.5 }}>
                                                {q.authorName}:
                                            </Typography>
                                            <LinkableText text={q.text} sx={{ color: '#fff', fontSize: 14 }} />
                                        </Box>
                                        {qaItems
                                            .filter(x => x.kind === 'answer' && x.questionId === q.id)
                                            .map((a, ai) => (
                                                <Box key={`a-${q.id}-${ai}`} sx={{ ml: 2, mt: 0.5, pl: 1.5, borderLeft: '3px solid rgba(29,152,231,0.5)' }}>
                                                    <LinkableText text={a.text} sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 13 }} />
                                                </Box>
                                            ))
                                        }
                                    </Box>
                                );
                            }
                            return null;
                        })
                    )}
                    <div ref={qaScrollRef} />
                </Box>
                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                        placeholder="Escribe tu pregunta..."
                        value={questionInput}
                        onChange={(e) => setQuestionInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendQuestion(); } }}
                        size="small" fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(255,255,255,0.1)', color: '#fff',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                            },
                        }}
                    />
                    <Button variant="contained" onClick={handleSendQuestion} sx={{ bgcolor: '#1D98E7', flexShrink: 0 }}>
                        Enviar
                    </Button>
                </Box>
            </Box>

            {/* Arriba a la derecha: carrito del comprador (se paga ahí mismo,
                sin salir del vivo) y salir, dos botones de la misma forma en
                una fila */}
            <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 25, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InCallCart storeId={storeId} variant="button" live />
                <Button variant="contained" onClick={() => navigate('/home')}
                    sx={{ bgcolor: '#c62828', color: '#fff', minWidth: 48, height: 40, '&:hover': { bgcolor: '#b71c1c' } }}
                    aria-label="Salir">
                    <CallEndIcon sx={{ fontSize: 28 }} />
                </Button>
            </Box>

            {callEnded && (
                <Box sx={{ position: 'absolute', top: 16, left: 0, right: 0, textAlign: 'center', zIndex: 25 }}>
                    <Typography sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', py: 1, px: 2, display: 'inline-block', borderRadius: 1 }}>
                        La transmisión ha finalizado.
                    </Typography>
                </Box>
            )}

        </Box>

        {/* Fuera del contenedor del swipe: los eventos del diálogo (portal)
            igual burbujean por el árbol de React */}
        <ProductQuickDialog
            open={!!selectedProduct}
            onClose={() => setSelectedProduct(null)}
            storeId={storeId}
            product={selectedProduct}
            origin="live"
        />
        </Box>
    );
};

const mapStateToProps = (state) => {
    return {
        customer: state.customer.customer,
    };
};

export default connect(mapStateToProps)(LiveViewer);
